import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Gauge, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WatchRow } from "@/components/WatchRow";
import { AddWatch } from "@/components/AddWatch";
import { ComplianceFamilies } from "@/components/ComplianceFamilies";
import { ApiKeysPanel } from "@/components/ApiKeysPanel";
import { ApiConnections } from "@/components/ApiConnections";
import { UnitCompliance } from "@/components/UnitCompliance";
import { ComplianceTrendDialog } from "@/components/ComplianceTrendDialog";
import { Expander } from "@/components/Expander";
import { ComplianceTemplateDownload } from "@/components/ComplianceTemplateDownload";
import { TimelineTracker } from "@/components/TimelineTracker";
import { ProjectDetailSheet } from "@/components/ProjectDetailSheet";
import { api, apiError, fmtLakhValue, fmtPct } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Count = ({ label, value, tone, testId }) => (
  <div className="bg-card p-4" data-testid={testId}>
    <div className="label-caps">{label}</div>
    <div
      className="font-data mt-1 text-2xl font-black tabular-nums"
      style={tone ? { color: tone } : undefined}
    >
      {value}
    </div>
  </div>
);

/**
 * Compliance Data — the one place compliance is filled.
 *
 * Everything here writes into the portal's single compliance store, so the Dashboard odometers,
 * the trend popup, the register row and the Data Inconsistency screen all quote the same figures.
 * There is no second way in: this screen, the workbook below it, and a plant system posting to the
 * API all land in the same two collections.
 */
export default function ComplianceData() {
  const { scopedPlant, canReadAll, isAdmin, shows } = useAuth();
  const [params] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [plant, setPlant] = useState(scopedPlant || params.get("plant") || "all");
  const [q, setQ] = useState(params.get("q") || "");
  const [trendId, setTrendId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const plantParam = plant === "all" ? "" : plant;

  const load = useCallback(() => {
    api
      .get("/compliance/tracked", { params: { plant: plantParam, q } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [plantParam, q]);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data.totals;
  const units = (meta?.plants || []).map((p) => p.code);

  return (
    <div className="p-5 sm:p-8" data-testid="compliance-data-page">
      <div className="label-caps">One store · every screen reads it</div>
      <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
        Compliance Data
      </h1>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Add a usecase, declare the three figures it is measured against, and fill its days — by
        hand, in Excel, or straight from your own system. Whatever is filled here is what the
        Dashboard odometers read; there is no second copy of a compliance figure anywhere in the
        portal.
      </p>

      <div className="mt-6 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
        <Count label="Usecases sending data" value={t.tracked} testId="total-tracked" />
        <Count label="Showing on the Dashboard" value={t.on_dashboard} testId="total-showing" />
        <Count
          label="Ready with all three targets"
          value={t.declared ?? 0}
          tone="hsl(var(--healthy))"
          testId="total-declared"
        />
        <Count label="Days of data captured" value={t.reading_days} testId="total-days" />
      </div>

      {shows("compliance_data.add") && (
        <div className="mt-6">
          <Expander
            testId="compliance-add-expander"
            title="Add Compliance Data for a new usecase"
            note="The first fill — its KPI, the three figures it is measured against, and the first day's reading with the date it is for."
            defaultOpen={!t.tracked}
          >
            <AddWatch plant={plantParam} onAdded={load} />
          </Expander>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Input
          className="h-11 min-w-[260px] flex-1 rounded-none"
          data-testid="compliance-search"
          placeholder="Search by code, usecase or KPI…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select value={plant} onValueChange={setPlant}>
          <SelectTrigger className="h-11 w-[200px] rounded-none" data-testid="compliance-plant">
            <SelectValue placeholder="Unit" />
          </SelectTrigger>
          <SelectContent className="max-h-80 rounded-none bg-popover">
            {(!scopedPlant || canReadAll) && <SelectItem value="all">All units I may see</SelectItem>}
            {(meta?.plants || []).map((p) => (
              <SelectItem key={p.code} value={p.code}>
                {p.code} — {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!data.rows.length ? (
        <div className="mt-6 border border-border bg-card p-8" data-testid="compliance-empty">
          <Gauge className="h-6 w-6 text-muted-foreground" />
          <h2 className="mt-3 font-display text-xl font-bold">
            No usecase is sending compliance data in this view yet
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Open “Add Compliance Data for a new usecase” above, pick one from the hot trial
            onwards, name its KPI and declare the target, the baseline and the target impact — then
            fill the first day with the date it is for. Its odometer stands on the Dashboard from
            that moment.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-px border border-border bg-border">
          {data.rows.map((card) => (
            <WatchRow
              key={card.project_id}
              card={card}
              onChanged={load}
              onTrend={setTrendId}
            />
          ))}
        </div>
      )}

      {data.rows.length > 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="compliance-footnote">
          Cumulative across this view {fmtPct(
            data.rows.reduce(
              (s, r) => s + (r.compliance.cumulative_pct || 0),
              0,
            ) / Math.max(1, data.rows.filter((r) => r.compliance.cumulative_pct !== null).length),
          )}{" "}
          · impact recorded{" "}
          {fmtLakhValue(data.rows.reduce((s, r) => s + (r.impact.total_lakh || 0), 0))}. Press a
          usecase to open its figures; press Trend for the picture.
        </p>
      )}

      <div className="mt-8 space-y-px bg-border">
        {shows("compliance_data.families") && (
          <Expander
            testId="compliance-families-expander"
            title="The same work at several units — side by side"
            note="One bar per unit on one scale, so a ferro-alloy model at BSL, BSP and RSP can be read against each other at a glance."
          >
            <ComplianceFamilies plant={plantParam} q={q} />
          </Expander>
        )}

        <Expander
          testId="compliance-units-expander"
          title="Unit by unit — what each plant is sending"
          note="Open a unit for its own usecases, the day each was last filled and who filled it."
        >
          <UnitCompliance rows={data.rows} />
        </Expander>

        <Expander
          testId="compliance-workbook-expander"
          title="Fill a whole unit in Excel"
          note="One sheet per unit, the three settle-once columns and one row per usecase per day."
        >
          <ComplianceTemplateDownload
            units={units}
            plantNames={Object.fromEntries((meta?.plants || []).map((p) => [p.code, p.name]))}
            myPlant={scopedPlant || ""}
          />
        </Expander>

        {shows("compliance_data.api") && (
          <Expander
            testId="compliance-api-expander"
            title="Connect your own system — automated compliance entry"
            note="A key per unit, a usecase switched on by SDTD, one POST a day or 500 days in one call — with the live sync status beside each usecase."
          >
            <ApiConnections plants={plantParam} />
            <div className="mt-6 border-t border-border pt-6">
              <ApiKeysPanel units={units} isAdmin={isAdmin} />
            </div>
          </Expander>
        )}
      </div>

      <TimelineTracker plant={plantParam} onOpen={setOpenId} />

      <ComplianceTrendDialog projectId={trendId} onClose={() => setTrendId(null)} />
      <ProjectDetailSheet
        projectId={openId}
        open={!!openId}
        onOpenChange={(v) => !v && setOpenId(null)}
        meta={meta}
        onChanged={load}
      />
    </div>
  );
}
